/**
 * 📝 [Phase 103 — 2026-07-09] Section "Mes évaluations" pour Mon Espace.
 *
 * Affiche à l'employé :
 *  - Liste de ses évaluations finalisées (pas les brouillons)
 *  - Détails de chaque évaluation (critères, note globale, textes libres)
 *  - Bouton "J'ai pris connaissance" (acknowledge) + commentaire optionnel
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Star,
  ClipboardCheck,
  Loader2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
} from "lucide-react";

const API_URL =
  (typeof window !== "undefined" && window.__API_URL__) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

function getAuthToken() {
  try {
    return (
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token") ||
      ""
    );
  } catch {
    return "";
  }
}
const authHeader = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function noteColor(note) {
  if (note == null) return "bg-slate-200 text-slate-500 border-slate-300";
  if (note < 2.5) return "bg-red-100 text-red-800 border-red-300";
  if (note < 3.5) return "bg-orange-100 text-orange-800 border-orange-300";
  if (note < 4) return "bg-yellow-100 text-yellow-800 border-yellow-300";
  return "bg-emerald-100 text-emerald-800 border-emerald-300";
}

function EvaluationDetail({ evalDoc, config, onAck }) {
  const [expanded, setExpanded] = useState(false);
  const [ackText, setAckText] = useState("");
  const [saving, setSaving] = useState(false);

  const note =
    evalDoc.note_globale_manuelle != null
      ? evalDoc.note_globale_manuelle
      : evalDoc.note_globale_calculee;

  const handleAck = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/evaluations/${evalDoc.id}/acknowledge`,
        { commentaire: ackText },
        { headers: authHeader() }
      );
      toast.success("Prise en connaissance enregistrée");
      onAck?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const categoriesConfigFallback = config?.categories || {};
  const echelle = config?.echelle || {};

  // 🆕 [Phase 103.1] Priorité au snapshot de l'éval si présent (critères customisés)
  const snapshotCats = evalDoc?.criteres_snapshot?.categories;
  const categoriesEntries = Array.isArray(snapshotCats) && snapshotCats.length
    ? snapshotCats.map((cat) => [cat.code, { label: cat.label, criteres: Object.fromEntries((cat.criteres || []).map((c) => [c.code, c.label])) }])
    : Object.entries(categoriesConfigFallback);

  return (
    <div
      className="border border-slate-200 rounded-lg bg-white overflow-hidden"
      data-testid={`mes-eval-item-${evalDoc.id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors text-left"
        data-testid={`mes-eval-toggle-${evalDoc.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm text-slate-800">
              Évaluation {evalDoc.annee}
            </p>
            {evalDoc.employee_acknowledged && (
              <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                Acquis
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {evalDoc.periode_debut} → {evalDoc.periode_fin} · Par{" "}
            {evalDoc.evaluateur_nom || "?"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            className={`text-sm font-bold border ${noteColor(note)}`}
            data-testid={`mes-eval-note-${evalDoc.id}`}
          >
            <Star className="w-3 h-3 mr-1" />
            {note != null ? `${note} / 5` : "—"}
          </Badge>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div
          className="border-t border-slate-100 p-4 space-y-4 bg-slate-50"
          data-testid={`mes-eval-detail-${evalDoc.id}`}
        >
          {/* Grille des critères */}
          {categoriesEntries.map(([catKey, catDef]) => {
            const catScores = evalDoc.criteres?.[catKey] || {};
            if (!Object.keys(catScores).length) return null;
            return (
              <div key={catKey} className="bg-white p-3 rounded-lg border border-slate-200">
                <p className="font-semibold text-xs text-slate-700 mb-2">
                  {catDef.label}
                </p>
                <div className="space-y-1">
                  {Object.entries(catDef.criteres).map(([critKey, critLabel]) => {
                    const v = catScores[critKey];
                    if (v == null) return null;
                    // 🆕 [Hotfix v13.36 — 2026-07-09] Bug #5 : commentaire textuel par critère (si présent)
                    const comm = (evalDoc.commentaires_criteres?.[catKey] || {})[critKey] || "";
                    return (
                      <div
                        key={critKey}
                        className="flex flex-col gap-0.5 text-xs text-slate-600 py-0.5"
                        data-testid={`mes-eval-crit-${evalDoc.id}-${catKey}-${critKey}`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{critLabel}</span>
                          <span className="font-medium text-slate-800">
                            {v}/5 — {echelle[v] || "?"}
                          </span>
                        </div>
                        {comm && (
                          <p
                            className="italic text-slate-500 pl-2 border-l-2 border-slate-200 whitespace-pre-wrap"
                            data-testid={`mes-eval-comm-${evalDoc.id}-${catKey}-${critKey}`}
                          >
                            {comm}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Textes libres */}
          {(evalDoc.points_forts ||
            evalDoc.axes_amelioration ||
            evalDoc.objectifs_suivants ||
            evalDoc.commentaires_evaluateur) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {evalDoc.points_forts && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <p className="font-semibold text-emerald-800 mb-1">
                    Points forts
                  </p>
                  <p className="text-emerald-900 whitespace-pre-wrap">
                    {evalDoc.points_forts}
                  </p>
                </div>
              )}
              {evalDoc.axes_amelioration && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2">
                  <p className="font-semibold text-orange-800 mb-1">
                    Axes d&apos;amélioration
                  </p>
                  <p className="text-orange-900 whitespace-pre-wrap">
                    {evalDoc.axes_amelioration}
                  </p>
                </div>
              )}
              {evalDoc.objectifs_suivants && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <p className="font-semibold text-blue-800 mb-1">
                    Objectifs pour la prochaine période
                  </p>
                  <p className="text-blue-900 whitespace-pre-wrap">
                    {evalDoc.objectifs_suivants}
                  </p>
                </div>
              )}
              {evalDoc.commentaires_evaluateur && (
                <div className="bg-slate-100 border border-slate-200 rounded p-2">
                  <p className="font-semibold text-slate-800 mb-1">
                    Commentaires du chef
                  </p>
                  <p className="text-slate-900 whitespace-pre-wrap">
                    {evalDoc.commentaires_evaluateur}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Acknowledge (optionnel) */}
          {!evalDoc.employee_acknowledged ? (
            <div className="border-t border-slate-200 pt-3">
              <p className="text-xs text-slate-600 mb-1">
                <em>Optionnel</em> — laisser un commentaire à ton évaluateur, puis
                confirmer avoir pris connaissance de l&apos;évaluation.
              </p>
              <Textarea
                rows={2}
                value={ackText}
                onChange={(e) => setAckText(e.target.value)}
                placeholder="Ton retour (facultatif)…"
                data-testid={`mes-eval-ack-text-${evalDoc.id}`}
              />
              <Button
                className="mt-2 bg-blue-600 hover:bg-blue-700"
                size="sm"
                onClick={handleAck}
                disabled={saving}
                data-testid={`mes-eval-ack-btn-${evalDoc.id}`}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                )}
                J&apos;ai pris connaissance
              </Button>
            </div>
          ) : (
            <div className="border-t border-slate-200 pt-3 text-xs text-slate-600">
              {evalDoc.employee_commentaire && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 mb-2">
                  <p className="font-semibold text-emerald-800">Ton commentaire :</p>
                  <p className="text-emerald-900 whitespace-pre-wrap">
                    {evalDoc.employee_commentaire}
                  </p>
                </div>
              )}
              <p className="text-emerald-700">
                ✓ Pris en connaissance le{" "}
                {new Date(evalDoc.employee_ack_date).toLocaleDateString("fr-CA")}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MesEvaluationsSection() {
  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [config, setConfig] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/mon-espace/me/evaluations`, {
        headers: authHeader(),
      });
      setEvaluations(res.data?.evaluations || []);
      setConfig(res.data?.config || null);
    } catch (err) {
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card data-testid="mes-evaluations-section">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardCheck className="w-4 h-4 text-slate-500" /> Mes évaluations
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : evaluations.length === 0 ? (
          <div
            className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600"
            data-testid="mes-evaluations-empty"
          >
            <ClipboardCheck className="w-4 h-4 text-slate-400" />
            Aucune évaluation publiée pour le moment.
          </div>
        ) : (
          <div className="space-y-2" data-testid="mes-evaluations-list">
            {evaluations.map((e) => (
              <EvaluationDetail
                key={e.id}
                evalDoc={e}
                config={config}
                onAck={load}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MesEvaluationsSection;
